import * as THREE from 'three';
import { SceneModule, SceneContext, EnvironmentState } from '../types';
import { CameraView } from '../../engine/cameraRig';
import { WeatherType } from '../../app/store';
import { StylizedWater } from '../shared/water';
import { disposeHierarchy } from '../../engine/disposal';
import { t } from '../../ui/i18n';
import { store } from '../../app/store';

export class LijiangScene implements SceneModule {
  public id: string = 'lijiang';
  public nameKey: string = 'scene_lijiang';

  public root: THREE.Group = new THREE.Group();
  private canalWater!: StylizedWater;
  private waterWheelGroup!: THREE.Group;
  private houseInstances!: THREE.InstancedMesh;
  private roofInstances!: THREE.InstancedMesh;
  private lanternInstances!: THREE.InstancedMesh;
  private floatingLanterns: THREE.Group[] = [];
  private cats: THREE.Group[] = [];

  private houseMat!: THREE.MeshStandardMaterial;
  private roofMat!: THREE.MeshStandardMaterial;
  private lanternMat!: THREE.MeshStandardMaterial;
  private waterwheelSpeed: number = 0.8;
  private showToastFn?: (msg: string) => void;

  public cameras: CameraView[] = [
    {
      id: 1,
      nameKey: 'view_lijiang_1',
      position: new THREE.Vector3(0, 28, 44),
      target: new THREE.Vector3(0, 4, 0),
      mode: 'orbit',
      minDistance: 10,
      maxDistance: 90,
    },
    {
      id: 2,
      nameKey: 'view_lijiang_2',
      position: new THREE.Vector3(-4, 2.2, 12),
      target: new THREE.Vector3(0, 2.0, 0),
      mode: 'orbit',
      minDistance: 3,
      maxDistance: 40,
    },
    {
      id: 3,
      nameKey: 'view_lijiang_3',
      position: new THREE.Vector3(6, 3.5, 6),
      target: new THREE.Vector3(0, 2.0, 0),
      mode: 'orbit',
      minDistance: 3,
      maxDistance: 45,
    },
    {
      id: 4,
      nameKey: 'view_lijiang_4',
      position: new THREE.Vector3(-14, 4.0, -10),
      target: new THREE.Vector3(-18, 5.0, -18),
      mode: 'orbit',
      minDistance: 4,
      maxDistance: 50,
    },
    {
      id: 5,
      nameKey: 'view_lijiang_5',
      position: new THREE.Vector3(22, 18, -12),
      target: new THREE.Vector3(18, 14, -18),
      mode: 'orbit',
      minDistance: 6,
      maxDistance: 70,
    },
    {
      id: 6,
      nameKey: 'view_lijiang_6',
      position: new THREE.Vector3(0, 10, 25),
      target: new THREE.Vector3(0, 8, 0),
      mode: 'fly',
    },
  ];

  public async load(ctx: SceneContext, onProgress: (p: number) => void): Promise<void> {
    this.showToastFn = ctx.showToast;
    this.root.name = 'LijiangOldTownScene';

    onProgress(0.1);
    this.createMaterials();

    onProgress(0.25);
    this.createGroundAndCanals();

    onProgress(0.45);
    this.createNaxiHouses();

    onProgress(0.65);
    this.createWaterwheels();
    this.createWangguTower();

    onProgress(0.8);
    this.createBridgesAndWillows();
    this.createDistantSnowMountain();
    this.createCats();

    // Setup interactive raycasting clicks
    this.setupInteractivity(ctx);

    ctx.sceneRoot.add(this.root);
    onProgress(1.0);
  }

  private createMaterials(): void {
    // Naxi timber wall
    this.houseMat = new THREE.MeshStandardMaterial({
      color: 0x854d0e, // warm weathered wood
      roughness: 0.85,
      metalness: 0.05,
      flatShading: true,
    });

    // Traditional grey-black clay tiles
    this.roofMat = new THREE.MeshStandardMaterial({
      color: 0x334155, // slate dark tile
      roughness: 0.6,
      metalness: 0.15,
      flatShading: true,
    });

    // Glowing red paper lanterns
    this.lanternMat = new THREE.MeshStandardMaterial({
      color: 0xdc2626,
      emissive: 0xf97316,
      emissiveIntensity: 0.8,
      roughness: 0.5,
    });
  }

  private createGroundAndCanals(): void {
    // Cobblestone ground
    const groundGeo = new THREE.BoxGeometry(70, 2, 70);
    groundGeo.translate(0, -1, 0);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x64748b, // stone grey
      roughness: 0.7,
      flatShading: true,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.receiveShadow = true;
    this.root.add(ground);

    // Flowing Canal Water
    this.canalWater = new StylizedWater({
      width: 10,
      height: 60,
      deepColor: 0x0369a1,
      shallowColor: 0x38bdf8,
    });
    this.canalWater.mesh.position.set(0, 0.05, 0);
    this.root.add(this.canalWater.mesh);

    // Stone canal embankments
    const bankMat = new THREE.MeshStandardMaterial({ color: 0x475569, roughness: 0.8 });
    const bankGeoL = new THREE.BoxGeometry(0.8, 0.4, 60);
    const bankL = new THREE.Mesh(bankGeoL, bankMat);
    bankL.position.set(-5.4, 0.2, 0);
    this.root.add(bankL);

    const bankGeoR = new THREE.BoxGeometry(0.8, 0.4, 60);
    const bankR = new THREE.Mesh(bankGeoR, bankMat);
    bankR.position.set(5.4, 0.2, 0);
    this.root.add(bankR);
  }

  private createNaxiHouses(): void {
    const houseCount = 42;
    const bodyGeo = new THREE.BoxGeometry(4.5, 4.0, 4.5);
    const roofGeo = new THREE.ConeGeometry(3.8, 1.8, 4);
    roofGeo.rotateY(Math.PI / 4);

    this.houseInstances = new THREE.InstancedMesh(bodyGeo, this.houseMat, houseCount);
    this.roofInstances = new THREE.InstancedMesh(roofGeo, this.roofMat, houseCount);
    this.lanternInstances = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.35, 8, 8),
      this.lanternMat,
      houseCount * 2
    );

    this.houseInstances.castShadow = true;
    this.houseInstances.receiveShadow = true;
    this.roofInstances.castShadow = true;

    const dummy = new THREE.Object3D();
    const lanternDummy = new THREE.Object3D();
    let idx = 0;
    let lanternIdx = 0;

    // Place houses on left and right banks of the canal
    for (let row = -3; row <= 3; row++) {
      for (let col = -3; col <= 3; col++) {
        const x = col * 8 + (col > 0 ? 6 : -6);
        const z = row * 8;

        // Skip canal corridor
        if (Math.abs(x) < 8) continue;
        if (idx >= houseCount) break;

        // Body
        dummy.position.set(x, 2.0, z);
        dummy.rotation.y = (Math.PI / 2) * Math.floor(Math.random() * 4);
        dummy.scale.setScalar(0.9 + Math.random() * 0.2);
        dummy.updateMatrix();
        this.houseInstances.setMatrixAt(idx, dummy.matrix);

        // Roof with carved eaves
        dummy.position.set(x, 4.8, z);
        dummy.scale.set(1.2, 1.0, 1.2);
        dummy.updateMatrix();
        this.roofInstances.setMatrixAt(idx, dummy.matrix);

        // Hanging lanterns under eaves
        lanternDummy.position.set(x + (col > 0 ? -1.8 : 1.8), 3.8, z - 1.5);
        lanternDummy.updateMatrix();
        this.lanternInstances.setMatrixAt(lanternIdx++, lanternDummy.matrix);

        lanternDummy.position.set(x + (col > 0 ? -1.8 : 1.8), 3.8, z + 1.5);
        lanternDummy.updateMatrix();
        this.lanternInstances.setMatrixAt(lanternIdx++, lanternDummy.matrix);

        idx++;
      }
    }

    this.houseInstances.instanceMatrix.needsUpdate = true;
    this.roofInstances.instanceMatrix.needsUpdate = true;
    this.lanternInstances.instanceMatrix.needsUpdate = true;

    this.root.add(this.houseInstances);
    this.root.add(this.roofInstances);
    this.root.add(this.lanternInstances);
  }

  private createWaterwheels(): void {
    // Iconic Great Waterwheels at entrance
    this.waterWheelGroup = new THREE.Group();
    this.waterWheelGroup.position.set(-18, 4.5, -18);

    const wheelMat = new THREE.MeshStandardMaterial({
      color: 0x451a03,
      roughness: 0.9,
      metalness: 0.1,
    });

    for (let w = 0; w < 2; w++) {
      const singleWheel = new THREE.Group();
      singleWheel.position.x = w * 2.2;

      // Outer rim
      const rimGeo = new THREE.TorusGeometry(3.5 - w * 0.6, 0.25, 8, 24);
      const rim = new THREE.Mesh(rimGeo, wheelMat);
      singleWheel.add(rim);

      // Radial spokes
      for (let s = 0; s < 12; s++) {
        const spokeGeo = new THREE.CylinderGeometry(0.08, 0.08, 6.8 - w * 1.2);
        const spoke = new THREE.Mesh(spokeGeo, wheelMat);
        spoke.rotation.z = (s * Math.PI) / 6;
        singleWheel.add(spoke);
      }

      this.waterWheelGroup.add(singleWheel);
    }

    this.root.add(this.waterWheelGroup);
  }

  private createWangguTower(): void {
    // Multi-tiered pagoda on Lion Hill (Wanggu Tower / Vạn Cổ Lâu)
    const towerGroup = new THREE.Group();
    towerGroup.position.set(18, 0, -18);

    // Hill base
    const hillGeo = new THREE.ConeGeometry(14, 8, 8);
    hillGeo.translate(0, 4, 0);
    const hillMat = new THREE.MeshStandardMaterial({ color: 0x1e3a29, roughness: 0.9 });
    const hill = new THREE.Mesh(hillGeo, hillMat);
    towerGroup.add(hill);

    // 4-tiered pagoda
    for (let tier = 0; tier < 4; tier++) {
      const y = 8 + tier * 2.5;
      const size = 5.0 - tier * 0.9;

      // Floor body
      const bodyGeo = new THREE.BoxGeometry(size, 2.0, size);
      const body = new THREE.Mesh(bodyGeo, this.houseMat);
      body.position.y = y + 1.0;
      towerGroup.add(body);

      // Upturned flying eaves roof
      const roofGeo = new THREE.ConeGeometry(size * 0.95, 1.4, 4);
      roofGeo.rotateY(Math.PI / 4);
      const roof = new THREE.Mesh(roofGeo, this.roofMat);
      roof.position.y = y + 2.4;
      towerGroup.add(roof);
    }

    this.root.add(towerGroup);
  }

  private createBridgesAndWillows(): void {
    // Arch stone bridges over the canal
    const bridgeMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.7 });
    const bridgePositions = [new THREE.Vector3(0, 0.4, 4), new THREE.Vector3(0, 0.4, -14)];

    for (const bPos of bridgePositions) {
      const arch = new THREE.Group();
      arch.position.copy(bPos);

      const spanGeo = new THREE.BoxGeometry(11.2, 0.5, 3.2);
      const span = new THREE.Mesh(spanGeo, bridgeMat);
      span.position.y = 1.0;
      arch.add(span);

      // Balustrades
      const railL = new THREE.Mesh(new THREE.BoxGeometry(11.2, 0.7, 0.3), bridgeMat);
      railL.position.set(0, 1.5, -1.4);
      arch.add(railL);

      const railR = new THREE.Mesh(new THREE.BoxGeometry(11.2, 0.7, 0.3), bridgeMat);
      railR.position.set(0, 1.5, 1.4);
      arch.add(railR);

      this.root.add(arch);
    }

    // Weeping willow trees along the canal banks
    const willowMat = new THREE.MeshStandardMaterial({
      color: 0x4ade80,
      roughness: 0.8,
      flatShading: true,
    });
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.9 });

    const willowCoords = [
      [-6.5, 6],
      [6.5, -4],
      [-6.5, -10],
      [6.5, 14],
    ];

    for (const [wx, wz] of willowCoords) {
      const willow = new THREE.Group();
      willow.position.set(wx, 0, wz);

      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.35, 3, 5), trunkMat);
      trunk.position.y = 1.5;
      willow.add(trunk);

      // Weeping dome foliage
      const foliage = new THREE.Mesh(new THREE.SphereGeometry(2.2, 7, 6), willowMat);
      foliage.scale.set(1.0, 1.4, 1.0);
      foliage.position.y = 3.6;
      willow.add(foliage);

      this.root.add(willow);
    }
  }

  private createDistantSnowMountain(): void {
    // Distant mountain backdrop to the North matching Scene 1's silhouette
    const distMtn = new THREE.Group();
    distMtn.position.set(0, 0, -48);

    const rockMat = new THREE.MeshStandardMaterial({ color: 0x475569, flatShading: true });
    const snowMat = new THREE.MeshStandardMaterial({ color: 0xf1f5f9, flatShading: true });

    for (let i = -3; i <= 3; i++) {
      const h = 26 - Math.abs(i) * 3.5;
      const r = 9 - Math.abs(i) * 0.8;
      const rock = new THREE.Mesh(new THREE.ConeGeometry(r, h, 5), rockMat);
      rock.position.set(i * 11, h / 2, 0);
      distMtn.add(rock);

      const snow = new THREE.Mesh(new THREE.ConeGeometry(r * 0.6, h * 0.5, 5), snowMat);
      snow.position.set(i * 11, h * 0.75, 0);
      distMtn.add(snow);
    }

    this.root.add(distMtn);
  }

  private createCats(): void {
    // Stylized low-poly cats sitting by the bridge
    const catGroup = new THREE.Group();
    catGroup.position.set(4.5, 0.3, 3.2);

    const catMat = new THREE.MeshStandardMaterial({ color: 0xf97316, roughness: 0.8 }); // ginger cat
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.3, 0.6), catMat);
    body.position.y = 0.2;
    catGroup.add(body);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 0.3), catMat);
    head.position.set(0, 0.45, 0.35);
    catGroup.add(head);

    this.root.add(catGroup);
    this.cats.push(catGroup);
  }

  private setupInteractivity(ctx: SceneContext): void {
    const canvas = ctx.renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onPointerDown = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, ctx.camera);

      // Check waterwheel click (Toy 1: Spin waterwheel faster)
      const wheelHits = raycaster.intersectObjects(this.waterWheelGroup.children, true);
      if (wheelHits.length > 0) {
        this.waterwheelSpeed = 4.2;
        this.showToastFn?.(t('toy_waterwheel_spun', store.getState().language));
        return;
      }

      // Check canal click (Toy 2: Release water lantern)
      const canalHits = raycaster.intersectObject(this.canalWater.mesh);
      if (canalHits.length > 0) {
        this.spawnFloatingLantern(canalHits[0].point);
        this.showToastFn?.(t('toy_lantern_released', store.getState().language));
      }
    };

    if (canvas && typeof canvas.addEventListener === 'function') {
      canvas.addEventListener('pointerdown', onPointerDown);
    }
  }

  private spawnFloatingLantern(pos: THREE.Vector3): void {
    const lantern = new THREE.Group();
    lantern.position.set(pos.x, 0.2, pos.z);

    const lotusGeo = new THREE.CylinderGeometry(0.5, 0.2, 0.2, 6);
    const lotusMat = new THREE.MeshStandardMaterial({
      color: 0xf43f5e, // lotus pink
      roughness: 0.6,
    });
    const lotus = new THREE.Mesh(lotusGeo, lotusMat);
    lantern.add(lotus);

    // Candle flame
    const candleGeo = new THREE.SphereGeometry(0.18, 6, 6);
    const candleMat = new THREE.MeshStandardMaterial({
      color: 0xffedd5,
      emissive: 0xfbbf24,
      emissiveIntensity: 1.8,
    });
    const flame = new THREE.Mesh(candleGeo, candleMat);
    flame.position.y = 0.25;
    lantern.add(flame);

    this.root.add(lantern);
    this.floatingLanterns.push(lantern);
  }

  public update(dt: number, env: EnvironmentState): void {
    // Waterwheel rotation
    if (this.waterWheelGroup) {
      if (this.waterwheelSpeed > 0.8) {
        this.waterwheelSpeed = Math.max(0.8, this.waterwheelSpeed - dt * 1.5);
      }
      this.waterWheelGroup.rotation.x += this.waterwheelSpeed * dt;
    }

    // Emissive lantern intensity based on time of day (dusk and night)
    if (this.lanternMat) {
      this.lanternMat.emissiveIntensity = 0.2 + env.emissiveFactor * 1.8;
    }

    // Float lanterns downstream along Z axis
    for (let i = this.floatingLanterns.length - 1; i >= 0; i--) {
      const fl = this.floatingLanterns[i];
      fl.position.z += dt * 1.8;
      // Gentle bobbing
      fl.position.y = 0.18 + Math.sin(fl.position.z * 1.5) * 0.04;

      if (fl.position.z > 28) {
        this.root.remove(fl);
        this.floatingLanterns.splice(i, 1);
      }
    }

    this.canalWater.update(dt);
  }

  public setTimeOfDay(_t: number): void {}
  public setWeather(_w: WeatherType): void {}

  public getSuggestedTracks(): string[] {
    return ['guzheng-night-lanterns', 'lijiang-canal-dusk'];
  }

  public onToyAction(toyId: string): void {
    if (toyId === 'wheel') {
      this.waterwheelSpeed = 4.2;
      this.showToastFn?.(t('toy_waterwheel_spun', store.getState().language));
    } else if (toyId === 'lantern') {
      this.spawnFloatingLantern(new THREE.Vector3(0, 0.2, -18));
      this.showToastFn?.(t('toy_lantern_released', store.getState().language));
    }
  }

  public dispose(): void {
    disposeHierarchy(this.root);
    this.canalWater.dispose();
    this.floatingLanterns = [];
  }
}
